/**
 * Default (non-iOS) audio playback via a shared HTMLAudioElement.
 */

const SILENT_MP3 =
  "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjQ1AAAAAAAAAAAAAAAAJAAAAAAAAYKb+pkTAAAAAAD/+0DEAAAHAAGf/+AIIgAA0gAAAAABNlbS4xMDAA";

let sharedAudio = null;
let currentObjectUrl = null;
let isUnlocked = false;

const createSharedAudio = () => {
  if (sharedAudio) return sharedAudio;

  sharedAudio = new Audio();
  sharedAudio.preload = "auto";
  sharedAudio.playsInline = true;
  sharedAudio.setAttribute("playsinline", "");
  sharedAudio.setAttribute("webkit-playsinline", "");

  return sharedAudio;
};

const revokeCurrentUrl = () => {
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }
};

/**
 * Call synchronously inside a click/tap handler before any await.
 */
export const unlockAudioPlayback = () => {
  const audio = createSharedAudio();
  if (isUnlocked) return;

  audio.src = SILENT_MP3;
  const playPromise = audio.play();
  if (!playPromise) return;

  playPromise
    .then(() => {
      isUnlocked = true;
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute("src");
      audio.load();
    })
    .catch((error) => {
      console.warn("Audio unlock failed:", error);
    });
};

export const getSharedAudioElement = () => createSharedAudio();

export const playBase64MpegAudio = (base64, callbacks = {}) => {
  const audio = createSharedAudio();
  revokeCurrentUrl();

  const audioBlob = new Blob(
    [Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))],
    { type: "audio/mpeg" }
  );
  currentObjectUrl = URL.createObjectURL(audioBlob);

  audio.volume = 1.0;
  audio.muted = false;
  audio.onplay = () => callbacks.onPlay?.();
  audio.onended = () => {
    revokeCurrentUrl();
    callbacks.onEnded?.();
  };
  audio.onerror = (event) => {
    revokeCurrentUrl();
    callbacks.onError?.(event);
  };

  audio.src = currentObjectUrl;
  audio.load();

  return new Promise((resolve, reject) => {
    let hasAttemptedPlay = false;

    const attemptPlay = () => {
      if (hasAttemptedPlay) return;
      hasAttemptedPlay = true;

      audio
        .play()
        .then(() => resolve(audio))
        .catch((error) => {
          callbacks.onError?.(error);
          reject(error);
        });
    };

    const timeoutId = setTimeout(() => {
      if (!hasAttemptedPlay) {
        console.warn("Audio ready event timeout, attempting playback anyway");
        attemptPlay();
      }
    }, 3000);

    const playWhenReady = () => {
      clearTimeout(timeoutId);
      attemptPlay();
    };

    audio.addEventListener("canplaythrough", playWhenReady, { once: true });
    audio.addEventListener("loadeddata", playWhenReady, { once: true });
  });
};

export const stopAudioPlayback = () => {
  if (!sharedAudio) return;

  sharedAudio.pause();
  sharedAudio.onplay = null;
  sharedAudio.onended = null;
  sharedAudio.onerror = null;
  revokeCurrentUrl();
};

export const releaseAudioPlayback = () => {
  stopAudioPlayback();

  if (sharedAudio) {
    sharedAudio.removeAttribute("src");
    sharedAudio.load();
  }

  isUnlocked = false;
  sharedAudio = null;
};
